import { useState } from 'react';
import { type RestaurantDetails } from '@discover-data/stores/restaurant/gql/getters/restaurantDetails';
import MenuPhotoSwipe from '@discover-components/restaurant/menu/photoSwipe';
import useLightbox from '@discover-components/restaurant/menu/useLightbox';
import {
  findMaxLastEdited,
  formatMenuImage,
  getMenuPages,
} from '@discover-components/restaurant/menu/utils';
import CameraIcon from '@discover-ui/icons/camera-1.svg';
import BookIcon from '@discover-ui/icons/book-open-01.svg';
import Button from '@discover-ui/button';
import { cn } from '@discover-lib/utils';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';

type LibraryKind = 'photos' | 'menu';

type MediaButtonProps = {
  label: string;
  icon: React.ReactNode;
  stretch?: boolean;
  onClick: () => void;
};

function MediaButton({ label, icon, stretch, onClick }: MediaButtonProps) {
  return (
    <Button
      type="button"
      onClick={onClick}
      kind="gray"
      className={cn(
        'gap-1 rounded-xl bg-ds-gray-light-50 px-5 py-2 text-xs font-semibold text-gray-600 pointer-fine:hover:bg-ds-gray-light-100',
        stretch && 'sm:flex-1'
      )}>
      {icon}
      {label}
    </Button>
  );
}

export type RestaurantCardMediaButtonsProps = {
  images: RestaurantDetails['images'];
  menus: RestaurantDetails['menus'];
  /** From `sm` up, splits the row's width between the buttons so they line up with the chips below. */
  stretch?: boolean;
};

export default function RestaurantCardMediaButtons({
  images,
  menus,
  stretch = false,
}: RestaurantCardMediaButtonsProps) {
  // A single PhotoSwipe instance is shared between both libraries (only one can be open at a time). It stays mounted so React StrictMode's dev remount doesn't tear down the viewer mid-open the way a lazily-mounted instance would.
  const [openLibrary, setOpenLibrary] = useState<LibraryKind | null>(null);
  const lightbox = useLightbox();
  const { track } = useAnalytics();

  const photoItems = (images ?? [])
    .filter((image) => image.url)
    .map((image) => formatMenuImage(image));

  const menuItems = menus ? getMenuPages(menus) : [];

  if (photoItems.length === 0 && menuItems.length === 0) {
    return null;
  }

  const open = (library: LibraryKind) => {
    if (library === 'menu') {
      track('clicks_menu_media_button', {});
    } else {
      track('clicks_photos_media_button', {});
    }
    setOpenLibrary(library);
    lightbox.open();
  };

  const close = () => {
    setOpenLibrary(null);
    lightbox.close();
  };

  const items = openLibrary === 'menu' ? menuItems : photoItems;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {photoItems.length > 0 && (
        <MediaButton
          label="Photos"
          icon={<CameraIcon className="size-5 text-gray-600" />}
          stretch={stretch}
          onClick={() => open('photos')}
        />
      )}
      {menuItems.length > 0 && (
        <MediaButton
          label="Menu"
          icon={<BookIcon className="size-5 text-gray-600" />}
          stretch={stretch}
          onClick={() => open('menu')}
        />
      )}
      <MenuPhotoSwipe
        items={items}
        isOpen={lightbox.isOpen}
        index={lightbox.index}
        showBackground={lightbox.showBackground}
        onClose={close}
        onChange={lightbox.onChange}
        lastUpdated={openLibrary === 'menu' ? findMaxLastEdited(menus) : undefined}
      />
    </div>
  );
}
